import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { BaseServices } from '../../kuba.services';
import {
  AddOnboardingCommentRequest,
  FlexiOnboardingAggregate,
  FlexiOnboardingFinalAction,
  FlexiOnboardingFinalStep,
  FlexiOnboardingFinalView,
  FlexiOnboardingMeeting,
  FlexiOnboardingRemovedDefaultStep,
  FlexiOnboardingStep,
  FlexiOnboardingStepComment,
  FlexiOnboardingSummary,
  NewFlexiOnboardingStepRequest,
  UpdateOnboardingStepRequest,
} from './flexi-onboarding.models';

/**
 * What the route resolver puts on the snapshot. The container component
 * uses `role` to pick which UI to render:
 *  - 'list'     → render the history grid of form records. This is what a CSM
 *                  gets on the bare tab route; the grid fetches its own rows.
 *  - 'csm'      → render the wizard authoring view from `aggregate`. Reached by
 *                  opening a specific record from the grid, so the route always
 *                  carries an `:id`.
 *  - 'customer' → render the final view from `finalView`.
 *  - 'locked'   → onboarding doesn't exist yet (typically a customer hitting
 *                  the page before the CSM has done anything).
 */
export interface FlexiOnboardingRouteData {
  role: 'list' | 'csm' | 'customer' | 'locked';
  aggregate?: FlexiOnboardingAggregate;
  /** Customer-facing bundle: cover meeting metadata + agreed actions + step content. */
  finalView?: FlexiOnboardingFinalView;
  /** Returned by /final but only populated post-completion. Empty until then. */
  isCompleted: boolean;
}

@Injectable({ providedIn: 'root' })
export class FlexiOnboardingService {
  private readonly base = `${environment.BASE_URL}/flexi/onboarding`;

  constructor(private http: HttpClient) {}

  // -------- CSM (HSE Advisor) endpoints --------
  //
  // A business owns a list of form records. Every authoring call therefore
  // addresses one record explicitly via /{businessId}/forms/{formId}/… —
  // "the business's onboarding" is no longer a unique thing to talk about.

  /** Base URL of one form record. */
  private form(businessId: number, formId: number): string {
    return `${this.base}/${businessId}/forms/${formId}`;
  }

  /** History grid rows for a business, newest first. */
  listForms(businessId: number): Observable<FlexiOnboardingSummary[]> {
    return this.http.get<any[]>(`${this.base}/${businessId}/forms`)
      .pipe(map(arr => (arr || []).map(r => this.mapSummary(r))));
  }

  /**
   * Starting content for a form that has NOT been created yet. Returns an
   * aggregate with id 0 so the wizard can open on a blank record; nothing is
   * written until the CSM saves.
   */
  getDraftForm(businessId: number): Observable<FlexiOnboardingAggregate> {
    return this.http.get<any>(`${this.base}/${businessId}/forms/draft`)
      .pipe(map(r => this.mapAggregate(r)));
  }

  /**
   * Creates a new form record. The server pre-fills it from the most recently
   * created one when the business has any, otherwise from the default template —
   * the same content `getDraftForm` returned, so materialising a draft on first
   * save produces the record the CSM has been looking at.
   * Existing records are never modified.
   */
  createForm(businessId: number): Observable<FlexiOnboardingAggregate> {
    return this.http.post<any>(`${this.base}/${businessId}/forms`, {})
      .pipe(map(r => this.mapAggregate(r)));
  }

  /** Loads one form record for the wizard. */
  getForm(businessId: number, formId: number): Observable<FlexiOnboardingAggregate> {
    return this.http.get<any>(this.form(businessId, formId)).pipe(map(r => this.mapAggregate(r)));
  }

  getStep(businessId: number, formId: number, stepKey: string): Observable<FlexiOnboardingStep> {
    return this.http.get<any>(`${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}`)
      .pipe(map(r => this.mapStep(r)));
  }

  updateStep(
    businessId: number, formId: number, stepKey: string, editableText: string | null,
  ): Observable<void> {
    const body: UpdateOnboardingStepRequest = { editableText };
    return this.http.put<void>(
      `${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}`, body);
  }

  saveAndNext(
    businessId: number, formId: number, stepKey: string, editableText: string | null,
  ): Observable<FlexiOnboardingAggregate> {
    const body: UpdateOnboardingStepRequest = { editableText };
    return this.http.post<any>(
      `${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}/save-and-next`, body)
      .pipe(map(r => this.mapAggregate(r)));
  }

  listComments(businessId: number, formId: number, stepKey: string): Observable<FlexiOnboardingStepComment[]> {
    return this.http.get<any[]>(
      `${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}/comments`)
      .pipe(map(arr => (arr || []).map(c => this.mapComment(c))));
  }

  addComment(
    businessId: number,
    formId: number,
    stepKey: string,
    body: AddOnboardingCommentRequest,
  ): Observable<FlexiOnboardingStepComment> {
    return this.http.post<any>(
      `${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}/comments`, body)
      .pipe(map(r => this.mapComment(r)));
  }

  deleteComment(businessId: number, formId: number, stepKey: string, commentId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}/comments/${commentId}`);
  }

  reopen(businessId: number, formId: number): Observable<FlexiOnboardingAggregate> {
    return this.http.post<any>(`${this.form(businessId, formId)}/reopen`, {})
      .pipe(map(r => this.mapAggregate(r)));
  }

  listRemovedDefaultSteps(businessId: number, formId: number): Observable<FlexiOnboardingRemovedDefaultStep[]> {
    return this.http.get<any[]>(`${this.form(businessId, formId)}/removed-default-steps`)
      .pipe(map(arr => (arr || []).map(s => this.mapRemovedDefault(s))));
  }

  restoreDefaultStep(businessId: number, formId: number, stepKey: string): Observable<FlexiOnboardingAggregate> {
    return this.http.post<any>(
      `${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}/restore`, {})
      .pipe(map(r => this.mapAggregate(r)));
  }

  addStep(
    businessId: number, formId: number, request: NewFlexiOnboardingStepRequest,
  ): Observable<FlexiOnboardingAggregate> {
    return this.http.post<any>(`${this.form(businessId, formId)}/steps`, request)
      .pipe(map(r => this.mapAggregate(r)));
  }

  removeStep(businessId: number, formId: number, stepKey: string): Observable<FlexiOnboardingAggregate> {
    return this.http.delete<any>(
      `${this.form(businessId, formId)}/steps/${encodeURIComponent(stepKey)}`)
      .pipe(map(r => this.mapAggregate(r)));
  }

  /**
   * Publishes one form. Completing a re-opened record overwrites that record,
   * while archiving an additional PDF — earlier PDFs stay available.
   */
  complete(businessId: number, formId: number): Observable<FlexiOnboardingAggregate> {
    return this.http.post<any>(`${this.form(businessId, formId)}/complete`, {})
      .pipe(map(r => this.mapAggregate(r)));
  }

  /**
   * Downloads an archived report PDF. Pass `formId` for the PDF a specific
   * record produced; omit it for the business's most recent one (the customer
   * view, which has no record to name).
   */
  downloadOhsAgreementPdf(businessId: number, formId?: number): Observable<Blob> {
    const query = formId ? `?onboardingId=${formId}` : '';
    return this.http.get(
      `${this.base}/${businessId}/ohs-agreement.pdf${query}`, { responseType: 'blob' });
  }

  // -------- Customer endpoint --------

  /**
   * Returns the customer-facing bundle for a completed onboarding.
   * Includes cover meeting metadata, the aggregated agreed-actions appendix,
   * and the per-step content. Each section can be independently empty when
   * the onboarding is not yet completed.
   */
  getFinalView(businessId: number): Observable<FlexiOnboardingFinalView> {
    return this.http.get<any>(`${this.base}/${businessId}/final`)
      .pipe(map(r => this.mapFinalView(r)));
  }

  // ==========================================================
  // Response normalizers
  // ----------------------------------------------------------
  // The backend currently serializes with PascalCase property
  // names (Id, Steps, StepKey, …). The frontend codebase is
  // camelCase. We pick whichever the server sent so this code
  // keeps working if/when the JSON config switches.
  // ==========================================================

  private mapAggregate = (r: any): FlexiOnboardingAggregate => {
    if (!r) return r;
    return {
      id: r.id ?? r.Id,
      businessId: r.businessId ?? r.BusinessId,
      status: r.status ?? r.Status,
      currentStep: r.currentStep ?? r.CurrentStep ?? 0,
      validUntil: r.validUntil ?? r.ValidUntil ?? null,
      isFirstForm: r.isFirstForm ?? r.IsFirstForm ?? false,
      steps: ((r.steps ?? r.Steps) || []).map((s: any) => this.mapStep(s)),
    };
  };

  private mapSummary = (r: any): FlexiOnboardingSummary => ({
    id: r.id ?? r.Id,
    businessId: r.businessId ?? r.BusinessId,
    status: r.status ?? r.Status ?? 0,
    createdOn: r.createdOn ?? r.CreatedOn,
    validUntil: r.validUntil ?? r.ValidUntil ?? null,
    createdBy: r.createdBy ?? r.CreatedBy ?? null,
    createdByName: r.createdByName ?? r.CreatedByName ?? null,
    modifiedBy: r.modifiedBy ?? r.ModifiedBy ?? null,
    modifiedByName: r.modifiedByName ?? r.ModifiedByName ?? null,
    modifiedOn: r.modifiedOn ?? r.ModifiedOn ?? null,
    completedOn: r.completedOn ?? r.CompletedOn ?? null,
  });

  private mapStep = (r: any): FlexiOnboardingStep => ({
    id: r.id ?? r.Id,
    stepKey: r.stepKey ?? r.StepKey,
    position: r.position ?? r.Position,
    isDefault: r.isDefault ?? r.IsDefault,
    title: r.title ?? r.Title,
    shortLabel: r.shortLabel ?? r.ShortLabel,
    subtitle: r.subtitle ?? r.Subtitle ?? null,
    templateText: r.templateText ?? r.TemplateText ?? null,
    editableText: r.editableText ?? r.EditableText ?? null,
    status: r.status ?? r.Status ?? 0,
  });

  private mapComment = (r: any): FlexiOnboardingStepComment => ({
    id: r.id ?? r.Id,
    commentText: r.commentText ?? r.CommentText,
    isPublic: r.isPublic ?? r.IsPublic ?? false,
    commentType: (r.commentType ?? r.CommentType ?? 0) as FlexiOnboardingStepComment['commentType'],
    responsibleParty: (r.responsibleParty ?? r.ResponsibleParty ?? null) as FlexiOnboardingStepComment['responsibleParty'],
    deadline: r.deadline ?? r.Deadline ?? null,
    createdBy: r.createdBy ?? r.CreatedBy ?? null,
    createdByName: r.createdByName ?? r.CreatedByName ?? null,
    createdOn: r.createdOn ?? r.CreatedOn,
  });

  private mapFinalStep = (r: any): FlexiOnboardingFinalStep => ({
    stepKey: r.stepKey ?? r.StepKey,
    position: r.position ?? r.Position,
    title: r.title ?? r.Title,
    shortLabel: r.shortLabel ?? r.ShortLabel,
    subtitle: r.subtitle ?? r.Subtitle ?? null,
    finalText: r.finalText ?? r.FinalText ?? null,
  });

  private mapRemovedDefault = (r: any): FlexiOnboardingRemovedDefaultStep => ({
    stepKey: r.stepKey ?? r.StepKey,
    title: r.title ?? r.Title,
    shortLabel: r.shortLabel ?? r.ShortLabel,
    subtitle: r.subtitle ?? r.Subtitle ?? null,
  });

  private mapFinalView = (r: any): FlexiOnboardingFinalView => {
    if (!r) return { meeting: null, actions: [], steps: [] };
    const meetingRaw = r.meeting ?? r.Meeting ?? null;
    const actionsRaw: any[] = r.actions ?? r.Actions ?? [];
    const stepsRaw: any[] = r.steps ?? r.Steps ?? [];
    return {
      meeting: meetingRaw ? this.mapMeeting(meetingRaw) : null,
      actions: actionsRaw.map(a => this.mapAction(a)),
      steps: stepsRaw.map(s => this.mapFinalStep(s)),
    };
  };

  private mapMeeting = (r: any): FlexiOnboardingMeeting => ({
    businessId: r.businessId ?? r.BusinessId,
    businessName: r.businessName ?? r.BusinessName ?? '',
    organisationNumber: r.organisationNumber ?? r.OrganisationNumber ?? null,
    advisorName: r.advisorName ?? r.AdvisorName ?? null,
    completedOn: r.completedOn ?? r.CompletedOn ?? null,
    validUntil: r.validUntil ?? r.ValidUntil ?? null,
    agreementId: r.agreementId ?? r.AgreementId ?? null,
  });

  private mapAction = (r: any): FlexiOnboardingFinalAction => ({
    id: r.id ?? r.Id,
    stepTitle: r.stepTitle ?? r.StepTitle ?? '',
    stepPosition: r.stepPosition ?? r.StepPosition ?? 0,
    commentText: r.commentText ?? r.CommentText ?? '',
    responsibleParty: (r.responsibleParty ?? r.ResponsibleParty ?? null) as FlexiOnboardingFinalAction['responsibleParty'],
    deadline: r.deadline ?? r.Deadline ?? null,
    createdByName: r.createdByName ?? r.CreatedByName ?? null,
    createdOn: r.createdOn ?? r.CreatedOn,
  });
}

/**
 * Resolver that decides which view to load — the CSM's history grid, one form
 * record in the authoring wizard, or the customer's final view — based on the
 * route's `:id` param and the BE response. The CSM endpoint is gated by the
 * `FlexiOnboardingCsmAuthor` policy (Admin bypass or
 * `BusinessSetting.HseAdviserKey == currentUserId`), so 403 means "not
 * authorised" — fall back to /final.
 */
@Injectable({ providedIn: 'root' })
export class FlexiOnboardingResolver {
  constructor(private service: FlexiOnboardingService) {}

  resolve(route: ActivatedRouteSnapshot): Observable<FlexiOnboardingRouteData> {
    const businessId = BaseServices.BusinessId;

    if (!this.couldBeCsm()) {
      return this.loadCustomer(businessId);
    }

    // The 'new' route opens the wizard on an unsaved draft. Creating the record
    // here instead would put a row in the grid the moment the CSM clicks New,
    // even if they immediately navigate away without entering anything.
    if (route.data['draft']) {
      return this.service.getDraftForm(businessId).pipe(
        map(aggregate => ({ role: 'csm' as const, aggregate, isCompleted: false })),
        catchError((err: HttpErrorResponse) => {
          if (err.status === 403) return this.loadCustomer(businessId);
          return throwError(() => err);
        }),
      );
    }

    // No `:id` on the route means the CSM opened the tab itself — show the
    // history grid rather than dropping them straight into a wizard. The grid
    // component fetches its own rows, so nothing needs resolving here.
    const formId = Number(route.paramMap.get('id'));
    if (!formId) {
      return of({ role: 'list' as const, isCompleted: false });
    }

    return this.service.getForm(businessId, formId).pipe(
      map(aggregate => ({ role: 'csm' as const, aggregate, isCompleted: aggregate.status === 2 })),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 403) return this.loadCustomer(businessId);
        return throwError(() => err);
      }),
    );
  }

  private loadCustomer(businessId: number): Observable<FlexiOnboardingRouteData> {
    return this.service.getFinalView(businessId).pipe(
      map(finalView => ({
        role: 'customer' as const,
        finalView,
        // Completion is anchored by the BE-supplied meeting metadata —
        // when null we know the onboarding is still locked.
        isCompleted: finalView.meeting !== null && finalView.steps.some(s => s.finalText != null),
      })),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 404) {
          return of({ role: 'locked' as const, finalView: { meeting: null, actions: [], steps: [] }, isCompleted: false });
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Cheap pre-flight that mirrors the BE policy bypass in
   * FlexiOnboardingOptions.cs: Admin (including admin-in-R-mode, whose
   * AbsoluteroleId stays '1') passes the CSM policy regardless of
   * HseAdviserKey, so we try the CSM endpoint first for them. Portal is
   * editor-equivalent in Flexi and is excluded; Editor / Guest / User /
   * Portal go straight to /final.
   */
  private couldBeCsm(): boolean {
    return String(BaseServices.AbsoluteroleId) === '1'; // Admin only
  }
}
